import { Injectable } from '@angular/core';
import { FirebaseService } from './firebase.service';
import { FirebaseAuthentication, SignInResult, User } from '@capacitor-firebase/authentication';
import { UserInfo } from 'firebase/auth';

@Injectable({
  providedIn: 'root',
})
export class MobileFirebaseService implements FirebaseService {
  async getUser(): Promise<User | null> {
    const res = await FirebaseAuthentication.getCurrentUser();
    return res.user;
  }


  /**
   * See FirebaseService.waitForRestoredUser.
   *
   * Two sources, whichever answers first: asking outright, and the plugin's
   * own authStateChange event. Asking alone would need polling; the event
   * alone never fires when the user was already restored before this call.
   *
   * The listener is removed on every exit, including the timeout. A listener
   * left behind on a screen somebody opens twice a day is a leak nobody sees.
   */
  async waitForRestoredUser(timeoutMs: number): Promise<User | null> {
    const alreadyThere = await this.getUser();
    if (alreadyThere) {
      return alreadyThere;
    }

    return new Promise<User | null>((resolve) => {
      let settled = false;
      let handle: { remove: () => Promise<void> } | undefined;

      const finish = (user: User | null) => {
        if (settled) {
          return;
        }
        settled = true;
        clearTimeout(timer);
        void handle?.remove();
        resolve(user);
      };

      const timer = setTimeout(() => finish(null), timeoutMs);

      void FirebaseAuthentication.addListener('authStateChange', (change) => {
        if (change?.user) {
          finish(change.user);
        }
      })
        .then((listener) => {
          handle = listener;
          if (settled) {
            // It resolved while the listener was being attached.
            void listener.remove();
          }
        })
        .catch(() => finish(null));
    });
  }

  async signInWithEmailAndPassword(email: string, password: string): Promise<string | undefined> {
    await FirebaseAuthentication.signInWithEmailAndPassword({ email, password });
    const idToken = await FirebaseAuthentication.getIdToken();
    return idToken.token;
  }
  async signInWithProvider(provider: string): Promise<string | undefined> {
    let res: SignInResult | undefined;
    if (provider === 'google') {
      res = await FirebaseAuthentication.signInWithGoogle();
    }
    if (provider === 'facebook') {
      res = await FirebaseAuthentication.signInWithFacebook();
    }
    if (provider === 'apple') {
      res = await FirebaseAuthentication.signInWithApple();
    }
    if (!res) {
      throw new Error('Provider not supported: ' + provider);
    }
    if (!res?.user) {
      throw new Error('User not found');
    }
    const idToken = await FirebaseAuthentication.getIdToken();
    return idToken.token;
  }
  async signUp(email: string, password: string): Promise<string | undefined> {
    await FirebaseAuthentication.createUserWithEmailAndPassword({ email, password });
    const idToken = await FirebaseAuthentication.getIdToken();
    return idToken.token;
  }
  async getIdToken(forceRefresh = false): Promise<string | undefined> {
    const idToken = await FirebaseAuthentication.getIdToken({ forceRefresh });
    return idToken.token;
  }
  async signOut(): Promise<void> {
    await FirebaseAuthentication.signOut();
  }
  async sendPasswordResetEmail(email: string): Promise<boolean> {
    await FirebaseAuthentication.sendPasswordResetEmail({ email });
    return true;
  }
  async signUpProvider(
    provider: string,
  ): Promise<{ token?: string; user?: Pick<UserInfo, 'email' | 'displayName' | 'photoURL'> }> {
    let res: SignInResult | undefined;
    if (provider === 'google') {
      res = await FirebaseAuthentication.signInWithGoogle();
    }
    if (provider === 'facebook') {
      res = await FirebaseAuthentication.signInWithFacebook();
    }
    if (provider === 'apple') {
      res = await FirebaseAuthentication.signInWithApple();
    }
    if (!res) {
      throw new Error('Provider not supported: ' + provider);
    }
    const idToken = await FirebaseAuthentication.getIdToken();
    return {
      token: idToken.token,
      user: res?.user
        ? { email: res.user.email, displayName: res.user.displayName, photoURL: res.user.photoUrl }
        : undefined,
    };
  }
}
